'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FilterSidebar, { FilterState } from './components/FilterSidebar';
import CollegeListingContent from './components/CollegeListingContent';
import { fetchColleges, College } from '@/lib/services/collegeService';

const defaultFilters: FilterState = {
  streams: [],
  states: [],
  cities: [],
  ownership: [],
  accreditation: [],
  feesMin: 0,
  feesMax: 5000000,
  nirfRankMax: 200,
  hostel: false,
  scholarships: false,
};

export default function CollegeListingsPage() {
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchColleges().then((data) => {
      setColleges(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Page Hero */}
        <div className="bg-secondary text-secondary-foreground py-8 px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">
                  Colleges in India 2026
                </h1>
                <p className="text-slate-300 text-sm">
                  Explore 25,000+ colleges — filter by stream, location, fees, and ranking to find your perfect match.
                </p>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="font-tabular font-bold text-white text-base">25,482</span>
                  <span>Colleges</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="font-tabular font-bold text-white text-base">38</span>
                  <span>States & UTs</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="font-tabular font-bold text-white text-base">120+</span>
                  <span>Streams</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
          <div className="flex gap-6">
            {/* Sidebar — desktop */}
            <aside className="hidden lg:block w-72 xl:w-80 shrink-0">
              <FilterSidebar onFiltersChange={setFilters} />
            </aside>

            {/* Mobile filter drawer */}
            {mobileFilterOpen && (
              <div className="fixed inset-0 z-50 lg:hidden">
                <div
                  className="absolute inset-0 bg-black/50"
                  onClick={() => setMobileFilterOpen(false)}
                />
                <div className="absolute left-0 top-0 bottom-0 w-80 bg-background overflow-y-auto shadow-xl">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <span className="font-bold text-foreground">Filters</span>
                    <button
                      onClick={() => setMobileFilterOpen(false)}
                      className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none"
                    >
                      ✕
                    </button>
                  </div>
                  <FilterSidebar onFiltersChange={(f) => { setFilters(f); }} />
                </div>
              </div>
            )}

            {/* Listings */}
            <div className="flex-1 min-w-0">
              <CollegeListingContent
                filters={filters}
                onOpenMobileFilters={() => setMobileFilterOpen(true)}
                colleges={colleges}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}